/**
 * BarcodeDisplay — renders a Code 128 barcode (the format every Five
 * Below in-store scanner reads) as a crisp SVG. Sized for both inline
 * thumbnails (passcode/coupon tile) and full-screen "show this to the
 * cashier" mode.
 *
 * Built 2026-06-15 alongside the Daily Passcode + Coupon features. Uses
 * `jsbarcode` (~30KB, dependency-free, generates SVG).
 */
import { useEffect, useRef } from "react";
import JsBarcode from "jsbarcode";

export default function BarcodeDisplay({ value, height = 80, width = 2, displayValue = true, testId }) {
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current || !value) return;
    try {
      JsBarcode(ref.current, value, {
        format: "CODE128",
        height,
        width,
        displayValue,
        fontSize: 14,
        margin: 4,
        background: "#ffffff",
        lineColor: "#0F172A",
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("BarcodeDisplay failed to render", value, e);
    }
  }, [value, height, width, displayValue]);

  if (!value) return null;
  // `max-width: 100%` lets the SVG scale DOWN on narrow viewports
  // (phones) when JsBarcode's natural pixel width (bars × `width`
  // px) exceeds the container — long passcode tokens were spilling
  // off the right edge of the BarcodeBottomSheet on iPhone (user
  // report 2026-06-18). `height: auto` preserves aspect ratio when
  // the SVG scales. `margin: 0 auto` centers the SVG horizontally
  // in its parent when the natural width is LESS than the container
  // (short 9-digit coupon codes on a tablet or wide-phone) — without
  // this the block-level SVG defaults to flush-left, which the user
  // reported 2026-07-02 as "coupons in the production app aren't
  // centered". No-op when SVG width == container width.
  return (
    <svg
      ref={ref}
      data-testid={testId || "barcode-svg"}
      style={{ maxWidth: "100%", height: "auto", display: "block", margin: "0 auto" }}
    />
  );
}
